
import React, { useState, useMemo } from 'react';
import { Company, CardConfig, CardFieldDef } from '../types';
import { api } from '../services/apiService';

interface CardConfigTabProps {
  company: Company;
  onCompanyUpdate: (updated: Company) => void;
}

// ─── Constantes ──────────────────────────────────────────────────────────────

const DEFAULT_FIELDS: CardFieldDef[] = [
  { key: 'name',              enabled: true  },
  { key: 'email',             enabled: true  },
  { key: 'phone',             enabled: false },
  { key: 'clientCompanyName', enabled: true  },
  { key: 'status',            enabled: true  },
  { key: 'potentialRevenue',  enabled: true  },
  { key: 'sectorActivity',    enabled: true  },
  { key: 'tag',               enabled: false },
  { key: 'createdAt',         enabled: true  },
  { key: 'agent',             enabled: true  },
];

const FIELD_LABELS: Record<CardFieldDef['key'], string> = {
  name:              'Nom',
  email:             'Email',
  phone:             'Téléphone',
  clientCompanyName: 'Entreprise',
  status:            'Statut',
  potentialRevenue:  'Revenu',
  sectorActivity:    'Secteur',
  tag:               'Tag',
  createdAt:         'Date',
  agent:             'Agent',
};

const FIELD_ICONS: Record<CardFieldDef['key'], string> = {
  name:              '👤',
  email:             '📧',
  phone:             '📞',
  clientCompanyName: '🏢',
  status:            '✓',
  potentialRevenue:  '💰',
  sectorActivity:    '🏷',
  tag:               '#',
  createdAt:         '📅',
  agent:             '👤',
};

const PREVIEW_COLORS: Record<CardFieldDef['key'], string> = {
  name:              '#e0eaff',
  email:             '#546a96',
  phone:             '#546a96',
  clientCompanyName: '#7f93bc',
  status:            '#6366f1',
  potentialRevenue:  '#10b981',
  sectorActivity:    '#7f93bc',
  tag:               '#11c5d6',
  createdAt:         '#3d5280',
  agent:             '#7f93bc',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Assigne des positions de grille aux champs activés qui n'en ont pas encore */
function assignDefaultPositions(fields: CardFieldDef[], gridCols: number): CardFieldDef[] {
  const occupied = new Set<string>();
  fields.forEach(f => {
    if (f.enabled && f.key !== 'name' && f.row !== undefined && f.col !== undefined) {
      occupied.add(`${f.row},${f.col}`);
    }
  });

  let r = 0, c = 0;
  const advance = () => {
    c++; if (c >= gridCols) { c = 0; r++; }
    while (occupied.has(`${r},${c}`)) { c++; if (c >= gridCols) { c = 0; r++; } }
  };
  while (occupied.has(`${r},${c}`)) advance();

  return fields.map(f => {
    if (!f.enabled || f.key === 'name' || (f.row !== undefined && f.col !== undefined)) return f;
    const pos = { row: r, col: c };
    occupied.add(`${r},${c}`);
    advance();
    return { ...f, ...pos };
  });
}

function getEffectiveConfig(company: Company): CardConfig {
  if (company.cardConfig?.fields?.length) {
    const gridCols = company.cardConfig.gridCols ?? 2;
    return {
      ...company.cardConfig,
      gridCols,
      fields: assignDefaultPositions(company.cardConfig.fields, gridCols),
    };
  }
  return {
    fields: assignDefaultPositions(DEFAULT_FIELDS, 2),
    showQuickCall: false,
    gridCols: 2,
  };
}

// ─── Prévisualisation d'un champ ─────────────────────────────────────────────

const PREVIEW_DATA: Record<string, string> = {
  email:             'ma•••@entreprise.fr',
  phone:             '06 •• •• •• 42',
  clientCompanyName: 'Acme Corp',
  status:            'Nouveau',
  potentialRevenue:  '+100K',
  sectorActivity:    'Tech',
  tag:               '#crm',
  createdAt:         '12 mars',
  agent:             'Laurent A.',
};

function FieldPreview({ fieldKey }: { fieldKey: CardFieldDef['key'] }) {
  const color = PREVIEW_COLORS[fieldKey];
  const data = PREVIEW_DATA[fieldKey] ?? '';
  return (
    <span
      className="text-[12px] font-bold truncate block"
      style={{ color }}
    >
      {FIELD_ICONS[fieldKey]} {data}
    </span>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────

const CardConfigTab: React.FC<CardConfigTabProps> = ({ company, onCompanyUpdate }) => {
  const [config, setConfig] = useState<CardConfig>(() => getEffectiveConfig(company));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // État du drag
  const [dragging, setDragging] = useState<{
    key: CardFieldDef['key'];
    fromRow: number | null;
    fromCol: number | null;
  } | null>(null);
  const [dragOverCell, setDragOverCell] = useState<{ row: number; col: number } | null>(null);
  const [dragOverPool, setDragOverPool] = useState(false);

  const save = async (newConfig: CardConfig) => {
    setSaving(true);
    setMessage(null);
    try {
      const updated = await api.updateCompany(company.id, { cardConfig: newConfig } as any);
      onCompanyUpdate(updated);
      setMessage({ type: 'success', text: 'Sauvegardé.' });
      setTimeout(() => setMessage(null), 2000);
    } catch {
      setMessage({ type: 'error', text: 'Erreur.' });
    } finally {
      setSaving(false);
    }
  };

  const applyAndSave = (newConfig: CardConfig) => {
    const positioned = assignDefaultPositions(newConfig.fields, newConfig.gridCols ?? 2);
    const finalConfig = { ...newConfig, fields: positioned };
    setConfig(finalConfig);
    save(finalConfig);
  };

  const toggleQuickCall = () => applyAndSave({ ...config, showQuickCall: !config.showQuickCall });

  const setGridCols = (cols: number) => {
    // Réinitialiser les positions pour le nouveau nombre de colonnes
    const reset = config.fields.map(f => ({ ...f, row: undefined, col: undefined } as CardFieldDef));
    applyAndSave({ ...config, gridCols: cols, fields: reset });
  };

  // ── Grille dérivée ──────────────────────────────────────────────────────────
  const gridCols = config.gridCols ?? 2;

  const { gridFields, disabledFields, maxRow } = useMemo(() => {
    const gf = config.fields.filter(f => f.key !== 'name' && f.enabled && f.row !== undefined && f.col !== undefined);
    const df = config.fields.filter(f => f.key !== 'name' && !f.enabled);
    const mr = gf.length > 0 ? Math.max(...gf.map(f => f.row!)) : -1;
    return { gridFields: gf, disabledFields: df, maxRow: mr };
  }, [config.fields]);

  // Nombre de lignes affichées = dernier champ + 1 ligne vide (pour pouvoir ajouter)
  const numRows = maxRow + 2;

  const getCellField = (row: number, col: number) =>
    gridFields.find(f => f.row === row && f.col === col) ?? null;

  // ── Handlers drag & drop ───────────────────────────────────────────────────

  const handleDragStartGrid = (field: CardFieldDef) => {
    setDragging({ key: field.key, fromRow: field.row!, fromCol: field.col! });
  };

  const handleDragStartPool = (field: CardFieldDef) => {
    setDragging({ key: field.key, fromRow: null, fromCol: null });
  };

  const handleCellDragOver = (e: React.DragEvent, row: number, col: number) => {
    e.preventDefault();
    setDragOverCell({ row, col });
    setDragOverPool(false);
  };

  const handlePoolDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverPool(true);
    setDragOverCell(null);
  };

  const handleCellDrop = (e: React.DragEvent, row: number, col: number) => {
    e.preventDefault();
    if (!dragging) return;
    const { key: dragKey, fromRow, fromCol } = dragging;

    const targetField = getCellField(row, col);

    const newFields = config.fields.map(f => {
      if (f.key === dragKey) {
        return { ...f, enabled: true, row, col };
      }
      if (targetField && f.key === targetField.key) {
        // Swap avec la cible
        if (fromRow !== null && fromCol !== null) {
          return { ...f, row: fromRow, col: fromCol };
        } else {
          // Vient du pool → désactiver le champ déplacé
          return { ...f, enabled: false, row: undefined, col: undefined };
        }
      }
      return f;
    });

    const newConfig = { ...config, fields: newFields };
    setConfig(newConfig);
    save(newConfig);
    setDragging(null);
    setDragOverCell(null);
  };

  const handlePoolDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (!dragging || dragging.fromRow === null) { setDragging(null); setDragOverPool(false); return; }

    const newFields = config.fields.map(f =>
      f.key === dragging.key ? { ...f, enabled: false, row: undefined, col: undefined } : f
    );

    const newConfig = { ...config, fields: newFields };
    setConfig(newConfig);
    save(newConfig);
    setDragging(null);
    setDragOverPool(false);
  };

  const handleDragEnd = () => {
    setDragging(null);
    setDragOverCell(null);
    setDragOverPool(false);
  };

  const setFieldAlign = (key: CardFieldDef['key'], align: 'left' | 'center' | 'right') => {
    const newFields = config.fields.map(f => f.key === key ? { ...f, align } : f);
    const newConfig = { ...config, fields: newFields };
    setConfig(newConfig);
    save(newConfig);
  };

  return (
    <div className="p-8 animate-in fade-in">

      {/* En-tête */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-2xl font-extrabold text-slate-900 mb-1 tracking-tight">Configuration des cartes</h3>
          <p className="text-slate-500 text-sm font-medium">
            Positionnez librement les champs sur la grille. Faites glisser depuis le pool pour activer un champ.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-4">
          {message && (
            <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg ${
              message.type === 'success'
                ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                : 'bg-rose-500/20 text-rose-300 border border-rose-500/30'
            }`}>{message.text}</span>
          )}
        </div>
      </div>

      <div className="flex gap-6 items-start">

        {/* ── Panneau gauche : outils ── */}
        <div className="w-52 flex-shrink-0 space-y-4">

          {/* Colonnes */}
          <div className="orion-settings-pref-card rounded-2xl p-4">
            <div className="text-[10px] font-black uppercase tracking-widest text-[#546a96] mb-3">Colonnes</div>
            <div className="flex gap-2">
              {[2, 3].map(n => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setGridCols(n)}
                  className={`flex-1 py-2 rounded-xl text-sm font-black transition-all ${
                    gridCols === n
                      ? 'bg-[rgba(17,197,214,0.2)] text-[#11c5d6] border border-[rgba(17,197,214,0.4)]'
                      : 'bg-[rgba(100,140,210,0.08)] text-[#546a96] border border-[rgba(100,140,210,0.15)] hover:bg-[rgba(100,140,210,0.15)]'
                  }`}
                >
                  {n} col.
                </button>
              ))}
            </div>
          </div>

          {/* Appel rapide */}
          <div className="orion-settings-pref-card rounded-2xl p-4">
            <div className="text-[10px] font-black uppercase tracking-widest text-[#546a96] mb-3">Actions</div>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-xs font-bold text-slate-900 mb-0.5">Appel rapide</div>
                <p className="text-[10px] text-slate-400">Bouton sur chaque carte</p>
              </div>
              <button
                type="button"
                disabled={saving}
                onClick={toggleQuickCall}
                title={config.showQuickCall ? 'Désactiver le bouton appel rapide' : 'Activer le bouton appel rapide'}
                aria-label={config.showQuickCall ? 'Désactiver le bouton appel rapide' : 'Activer le bouton appel rapide'}
                className={`relative flex-shrink-0 w-10 h-5 rounded-full transition-all duration-200 ${
                  config.showQuickCall ? 'bg-emerald-500' : 'bg-[rgba(100,140,210,0.2)]'
                } disabled:opacity-50`}
              >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${config.showQuickCall ? 'translate-x-5' : 'translate-x-0'}`} />
              </button>
            </div>
          </div>

          {/* Pool des champs désactivés */}
          <div className="orion-settings-pref-card rounded-2xl p-4">
            <div className="text-[10px] font-black uppercase tracking-widest text-[#546a96] mb-1">Champs disponibles</div>
            <p className="text-[10px] text-[#3d5280] mb-3">Faites glisser sur la grille pour activer</p>

            <div
              onDragOver={handlePoolDragOver}
              onDrop={handlePoolDrop}
              onDragLeave={() => setDragOverPool(false)}
              className={`min-h-[60px] rounded-xl border-2 border-dashed transition-all p-2 space-y-1.5 ${
                dragOverPool
                  ? 'border-rose-400/60 bg-rose-500/5'
                  : 'border-[rgba(100,140,210,0.2)] bg-[rgba(100,140,210,0.03)]'
              }`}
            >
              {disabledFields.length === 0 ? (
                <p className="text-[10px] text-[#3d5280] text-center py-2 italic">Tous les champs sont actifs</p>
              ) : (
                disabledFields.map(field => (
                  <div
                    key={field.key}
                    draggable
                    onDragStart={() => handleDragStartPool(field)}
                    onDragEnd={handleDragEnd}
                    className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border cursor-grab active:cursor-grabbing transition-all ${
                      dragging?.key === field.key
                        ? 'opacity-40 border-[rgba(100,140,210,0.3)] bg-[rgba(100,140,210,0.08)]'
                        : 'border-[rgba(100,140,210,0.2)] bg-[rgba(100,140,210,0.06)] hover:bg-[rgba(100,140,210,0.12)]'
                    }`}
                  >
                    <svg className="w-3 h-3 text-[#3d5280] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <circle cx="6" cy="5" r="1.5"/><circle cx="14" cy="5" r="1.5"/>
                      <circle cx="6" cy="10" r="1.5"/><circle cx="14" cy="10" r="1.5"/>
                      <circle cx="6" cy="15" r="1.5"/><circle cx="14" cy="15" r="1.5"/>
                    </svg>
                    <span className="text-[11px] text-[#546a96]">{FIELD_ICONS[field.key]}</span>
                    <span className="text-[11px] font-bold text-[#546a96] flex-1">{FIELD_LABELS[field.key]}</span>
                  </div>
                ))
              )}
              {dragOverPool && dragging?.fromRow !== null && (
                <div className="text-[10px] text-rose-400 text-center py-1 font-bold">Déposer pour désactiver</div>
              )}
            </div>
          </div>
        </div>

        {/* ── Zone centrale : grille ── */}
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-black uppercase tracking-widest text-[#546a96] mb-3 flex items-center gap-2">
            <span>Grille de positionnement</span>
            <span className="text-[#3d5280] font-normal normal-case">— glissez les champs pour les repositionner</span>
          </div>

          <div className="orion-settings-pref-card rounded-2xl overflow-hidden">
            {/* Ligne d'en-tête : nom (fixe, toujours en haut, pleine largeur) */}
            <div className="flex items-center gap-3 px-5 py-3.5 border-b border-[rgba(100,140,210,0.12)] bg-[rgba(100,140,210,0.06)]">
              <div className="w-7 h-7 rounded-lg bg-indigo-500/80 flex items-center justify-center text-[11px] font-black text-white flex-shrink-0">M</div>
              <div>
                <div className="text-[13px] font-bold text-[#e0eaff]">Marie Dupont</div>
                <div className="text-[9px] text-[#3d5280] font-bold uppercase tracking-widest">Nom du prospect — toujours visible</div>
              </div>
              {config.showQuickCall && (
                <div className="ml-auto w-7 h-7 rounded-lg bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400 flex-shrink-0">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                </div>
              )}
            </div>

            {/* Grille */}
            <div className="p-3">
              {Array.from({ length: numRows }).map((_, row) => (
                <div
                  key={row}
                  className="grid gap-2 mb-2"
                  style={{ gridTemplateColumns: `repeat(${gridCols}, 1fr)` }}
                >
                  {Array.from({ length: gridCols }).map((_, col) => {
                    const field = getCellField(row, col);
                    const isOver = dragOverCell?.row === row && dragOverCell?.col === col;
                    const isEmpty = !field;
                    const isDraggingThis = dragging?.key === field?.key;

                    return (
                      <div
                        key={col}
                        onDragOver={(e) => handleCellDragOver(e, row, col)}
                        onDrop={(e) => handleCellDrop(e, row, col)}
                        onDragLeave={() => setDragOverCell(null)}
                        className={`relative rounded-xl border-2 min-h-[48px] transition-all ${
                          isOver
                            ? field
                              ? 'border-amber-400/60 bg-amber-500/8'
                              : 'border-[rgba(17,197,214,0.6)] bg-[rgba(17,197,214,0.08)]'
                            : isEmpty
                              ? 'border-dashed border-[rgba(100,140,210,0.18)] bg-[rgba(100,140,210,0.02)] hover:border-[rgba(100,140,210,0.35)] hover:bg-[rgba(100,140,210,0.06)]'
                              : 'border-[rgba(100,140,210,0.2)] bg-[rgba(100,140,210,0.05)]'
                        } ${isDraggingThis ? 'opacity-40' : ''}`}
                      >
                        {field ? (
                          /* Cellule occupée : champ draggable */
                          <div className="flex flex-col h-full group/cell">
                            <div
                              draggable
                              onDragStart={() => handleDragStartGrid(field)}
                              onDragEnd={handleDragEnd}
                              className="flex items-center gap-2 px-3 pt-2.5 pb-1 flex-1 cursor-grab active:cursor-grabbing"
                            >
                              <svg className="w-3 h-3 text-[#2a3f6e] flex-shrink-0 opacity-0 group-hover/cell:opacity-100 transition-opacity" fill="currentColor" viewBox="0 0 20 20">
                                <circle cx="6" cy="5" r="1.5"/><circle cx="14" cy="5" r="1.5"/>
                                <circle cx="6" cy="10" r="1.5"/><circle cx="14" cy="10" r="1.5"/>
                                <circle cx="6" cy="15" r="1.5"/><circle cx="14" cy="15" r="1.5"/>
                              </svg>
                              <span className="text-base flex-shrink-0">{FIELD_ICONS[field.key]}</span>
                              <div className="min-w-0 flex-1">
                                <div className="text-[11px] font-black text-[#7f93bc] uppercase tracking-widest leading-none mb-0.5">{FIELD_LABELS[field.key]}</div>
                                <FieldPreview fieldKey={field.key} />
                              </div>
                            </div>
                            {/* Boutons d'alignement */}
                            <div className="flex items-center gap-0.5 px-2 pb-1.5 opacity-0 group-hover/cell:opacity-100 transition-opacity">
                              {(['left', 'center', 'right'] as const).map(a => (
                                <button
                                  key={a}
                                  type="button"
                                  title={a === 'left' ? 'Aligner à gauche' : a === 'center' ? 'Centrer' : 'Aligner à droite'}
                                  onClick={e => { e.stopPropagation(); setFieldAlign(field.key, a); }}
                                  className={`flex-1 flex items-center justify-center py-0.5 rounded transition-all ${
                                    (field.align ?? 'left') === a
                                      ? 'bg-[rgba(17,197,214,0.2)] text-[#11c5d6]'
                                      : 'bg-transparent text-[#3d5280] hover:text-[#546a96]'
                                  }`}
                                >
                                  {a === 'left' && (
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 6h18M3 12h12M3 18h15" />
                                    </svg>
                                  )}
                                  {a === 'center' && (
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 6h18M6 12h12M4.5 18h15" />
                                    </svg>
                                  )}
                                  {a === 'right' && (
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 6h18M9 12h12M6 18h15" />
                                    </svg>
                                  )}
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : (
                          /* Cellule vide : zone de dépôt */
                          <div className="flex items-center justify-center h-full min-h-[48px]">
                            {isOver ? (
                              <span className="text-[10px] font-bold text-[#11c5d6]">Déposer ici</span>
                            ) : (
                              <span className="text-[9px] text-[#2a3f6e] font-bold uppercase tracking-widest opacity-50">vide</span>
                            )}
                          </div>
                        )}

                        {/* Indicateur de swap */}
                        {isOver && field && (
                          <div className="absolute inset-0 rounded-xl border-2 border-amber-400/60 bg-amber-500/5 flex items-center justify-center pointer-events-none">
                            <span className="text-[10px] font-bold text-amber-400">↔ Échanger</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Panneau droit : prévisualisation ── */}
        <div className="flex-1 min-w-0 max-w-[50%]">
          <div className="text-[10px] font-black uppercase tracking-widest text-[#546a96] mb-3">Aperçu</div>
          <div
            className="orion-lead-card rounded-2xl border border-[rgba(100,140,210,0.2)] shadow-xl overflow-hidden"
            style={{ background: 'var(--card-bg, rgba(14,28,58,0.97))' }}
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-[rgba(100,140,210,0.1)]">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-black text-white bg-indigo-500 flex-shrink-0">M</div>
              <span className="font-bold text-[#e0eaff] text-[15px] truncate">Marie Dupont</span>
              {config.showQuickCall && (
                <div className="ml-auto w-8 h-8 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400 flex-shrink-0">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                </div>
              )}
            </div>

            {/* Grid preview */}
            <div
              className="p-4 gap-2"
              style={{ display: 'grid', gridTemplateColumns: `repeat(${gridCols}, 1fr)`, gridAutoRows: 'minmax(32px, auto)' }}
            >
              {gridFields.map(field => (
                <div
                  key={field.key}
                  className="px-3 py-2 rounded-xl bg-[rgba(100,140,210,0.06)] border border-[rgba(100,140,210,0.12)] flex items-center"
                  style={{
                    gridRowStart: (field.row ?? 0) + 1,
                    gridColumnStart: (field.col ?? 0) + 1,
                    justifyContent: field.align === 'center' ? 'center' : field.align === 'right' ? 'flex-end' : 'flex-start',
                  }}
                >
                  <FieldPreview fieldKey={field.key} />
                </div>
              ))}
            </div>

            {gridFields.length === 0 && (
              <div className="text-sm text-[#3d5280] italic text-center py-8 px-4">Aucun champ activé</div>
            )}
          </div>
          <p className="text-[10px] text-[#3d5280] mt-3 leading-relaxed">
            Aperçu de la disposition dans les vues <span className="font-bold">Cartes</span> et <span className="font-bold">Kanban</span>
          </p>
        </div>

      </div>
    </div>
  );
};

export { DEFAULT_FIELDS, getEffectiveConfig };
export type { CardConfig };
export default CardConfigTab;
